import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { SAR } from "../components/RiyalIcon";
import { ArrowLeft } from "lucide-react";
import { absoluteUrl } from "../components/ImageUpload";
import ProductQuantityControl from "../components/ProductQuantityControl";
import useRestaurantBrowserIdentity from "../hooks/useRestaurantBrowserIdentity";
import {
  getPublicRestaurant,
  prefetchPublicRestaurant,
} from "../lib/publicRestaurantResource";

export default function Cart() {
  const { slug, branchCode } = useParams();
  const nav = useNavigate();
  const cart = useCart();
  const branchId = cart.branch_id || null;
  const [isClosing, setIsClosing] = useState(false);
  const [restaurant, setRestaurant] = useState(null);
  const closeTimerRef = useRef(null);
  const publicBranchCode = branchCode || restaurant?.branch_code;
  const publicBranchBase = (
    `/r/${slug}${publicBranchCode ? `/${encodeURIComponent(publicBranchCode)}` : ""}`
  );

  useEffect(() => {
    if (cart.items.length === 0 || !slug) {
      return undefined;
    }

    prefetchPublicRestaurant(slug, branchId).catch(() => {});

    return undefined;
  }, [branchId, cart.items.length, slug]);

  useEffect(() => {
    let active = true;

    getPublicRestaurant(slug, branchId).then((data) => {
      if (active) {
        setRestaurant(data.restaurant);
      }
    }).catch(() => {
      if (active) {
        setRestaurant(null);
      }
    });

    return () => {
      active = false;
    };
  }, [branchId, slug]);

  useRestaurantBrowserIdentity(restaurant);

  useEffect(() => {
    return () => window.clearTimeout(closeTimerRef.current);
  }, []);

  const closeCart = () => {
    if (isClosing) return;

    setIsClosing(true);
    closeTimerRef.current = window.setTimeout(() => {
      nav(publicBranchBase);
    }, 150);
  };

  if (cart.items.length === 0) {
    return (
      <div
        className={`fixed inset-0 z-40 h-[100dvh] overflow-hidden bg-white grid place-items-center px-5 ${
          isClosing ? "cart-page-exit" : "cart-page-enter"
        }`}
        data-testid="cart-page"
      >
        <div className="text-center">
          <div className="text-6xl mb-4">🛒</div>
          <div className="font-extrabold text-xl mb-2">السلة فاضية</div>
          <Link
            to={publicBranchBase}
            onClick={(event) => {
              event.preventDefault();
              closeCart();
            }}
            className="pill brand-grad text-white px-5 py-3 font-bold inline-flex"
          >
            عودة للمنيو
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`fixed inset-0 z-40 flex h-[100dvh] flex-col overflow-hidden bg-white ${
        isClosing ? "cart-page-exit" : "cart-page-enter"
      }`}
      data-testid="cart-page"
    >
      <header
        className={
          "shrink-0 w-full max-w-md mx-auto flex items-center justify-start gap-3 px-5 pb-4 "
          + "pt-[calc(env(safe-area-inset-top)+1.5rem)] md:pt-6"
        }
        data-testid="cart-header"
      >
        <button
          onClick={closeCart}
          className="w-10 h-10 rounded-full bg-white grid place-items-center"
          data-testid="cart-back-button"
        >
          <ArrowLeft size={18} className="rotate-180" />
        </button>
        <h1 className="text-2xl font-extrabold">السلة</h1>
      </header>
      <main
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
        data-testid="cart-scroll-content"
      >
        <div className="max-w-md mx-auto px-5 pb-4 space-y-3">
          {cart.items.map((i) => (
            <div
              key={i._key}
              className={
                "flex min-h-20 w-full min-w-0 items-center gap-3 overflow-x-hidden rounded-2xl "
                + "border border-orange-100 bg-white p-3"
              }
              data-testid={`cart-item-${i.product_id}`}
            >
              <div className="h-14 w-14 shrink-0 overflow-hidden rounded-2xl food-img">
                {i.image_url && (
                  <img
                    src={absoluteUrl(i.image_url)}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                )}
              </div>
              <div className="flex min-w-0 flex-1 flex-col justify-center overflow-x-hidden">
                <div
                  className="text-right text-sm font-bold leading-4"
                  data-testid={`cart-product-name-${i.product_id}`}
                  style={{
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                    overflow: "hidden",
                  }}
                >
                  {i.name}
                </div>
                {(i.addon_snapshot || []).length > 0 && (
                  <div className="mt-0.5 truncate text-[10px] leading-3 text-gray-600">
                    {i.addon_snapshot.map((a, idx) => (
                      <span key={`${a.group_id || ""}-${a.option_id || a.name}-${idx}`}>
                        + {a.name}
                        {a.price_delta !== 0 && (
                          <span className="text-orange-600">
                            {" "}({a.price_delta > 0 ? "+" : "−"}
                            {" "}<SAR>{Math.abs(a.price_delta)}</SAR>)
                          </span>
                        )}
                        {idx < i.addon_snapshot.length - 1 && "، "}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-0.5 text-xs font-bold leading-4 text-orange-600">
                  <SAR>{cart.lineTotal(i)}</SAR>
                </div>
              </div>
              <ProductQuantityControl
                quantity={i.quantity}
                onDecrease={() => cart.setQty(i._key, i.quantity - 1)}
                onIncrease={() => cart.setQty(i._key, i.quantity + 1)}
                containerTestId={`cart-quantity-controls-${i.product_id}`}
                decreaseTestId={`cart-decrease-${i.product_id}`}
                increaseTestId={`cart-increase-${i.product_id}`}
                quantityTestId={`cart-quantity-${i.product_id}`}
                deleteIconTestId={`cart-delete-icon-${i.product_id}`}
                minusIconTestId={`cart-minus-icon-${i.product_id}`}
              />
            </div>
          ))}
          <label className="block" data-testid="cart-order-notes-section" dir="rtl">
            <span className="text-xs font-bold text-gray-600">
              ملاحظات على الطلب (اختياري)
            </span>
            <input
              value={cart.notes || ""}
              onChange={(event) => cart.setNotes(event.target.value)}
              className={
                "mt-1 h-12 w-full rounded-2xl border border-gray-200 bg-white px-4 "
                + "outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
              }
              data-testid="cart-order-notes"
            />
          </label>
        </div>
      </main>
      <div className="shrink-0" data-testid="cart-bottom-action">
        <div className="max-w-md mx-auto p-4">
          <div className="bg-white rounded-2xl card-shadow border border-orange-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-600">الإجمالي</span>
              <span className="text-2xl font-extrabold text-orange-600"><SAR>{cart.total}</SAR></span>
            </div>
            <button
              onClick={() => nav(`${publicBranchBase}/checkout`)}
              data-testid="go-checkout"
              className="w-full pill brand-grad text-white font-bold text-base py-3.5 tap-target"
            >
              متابعة الطلب
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
